<template>
  <div>
    <h1 class="headline mb-4 pt-4">
      Employees

      <v-btn
        small
        class="ml-2"
        color="primary"
        @click="showEmployeeDialog('Add')"
      >
        <v-icon small>add</v-icon>&nbsp;Add Employee
      </v-btn>
      <v-btn
        small
        class="ml-2"
        color="success"
        @click="downloadEmployeesCSV"
        :disabled="loading"
      >
        <v-icon small>download</v-icon>&nbsp;Download CSV
      </v-btn>
    </h1>
    <v-snackbar v-model="snack" :timeout="3000" :color="snackColor">
      {{ snackText }}
      <v-btn text @click="snack = false">Close</v-btn>
    </v-snackbar>
    <v-card>
      <v-tabs>
        <v-tab @click="switchEmployeeTab('active')"
          >Active
          <span class="caption ml-1 grey--text"
            >({{ activeEmployees.length }})</span
          ></v-tab
        >
        <v-tab @click="switchEmployeeTab('terminated')"
          >Terminated
          <span class="caption ml-1 grey--text"
            >({{ terminatedEmployees.length }})</span
          ></v-tab
        >
      </v-tabs>
    </v-card>
    <v-data-table
      :headers="headers"
      :items="filteredEmployees"
      class="elevation-1"
      :search="search"
      :loading="loading"
      :custom-filter="filterByName"
    >
      <template v-slot:top>
        <v-row class="mx-2 pt-6">
          <v-col cols="12" md="6" class="py-0">
            <v-text-field
              v-model="search"
              label="Filter by name or ID"
              outlined
              dense
            ></v-text-field>
          </v-col>
          <v-col cols="6" md="3" class="py-0">
            <v-text-field
              v-model="filters.rateMin"
              label="Rate Min"
              type="number"
              outlined
              dense
              clearable
              prepend-inner-icon="mdi-currency-usd"
            ></v-text-field>
          </v-col>
          <v-col cols="6" md="3" class="py-0">
            <v-text-field
              v-model="filters.rateMax"
              label="Rate Max"
              type="number"
              outlined
              dense
              clearable
              prepend-inner-icon="mdi-currency-usd"
            ></v-text-field>
          </v-col>
        </v-row>
      </template>
      <template v-slot:item.firstName="{ item }">
        {{ item.firstName }} {{ item.lastName }}
      </template>
      <template v-slot:item.effectiveDate="{ item }">
        <span v-if="item.effectiveDate">{{
          item.effectiveDate | moment("MMM Do YYYY")
        }}</span>
      </template>
      <template v-slot:item.payrollId="{ item }">
        {{ item.payroll.payrollId }}
      </template>
      <template v-slot:item.edit="{ item }">
        <v-btn
          fab
          dark
          x-small
          color="primary"
          @click="showEmployeeDialog('Edit', item)"
        >
          <v-icon dark>edit</v-icon>
        </v-btn>
      </template>
      <template v-slot:item.view="{ item }">
        <v-btn small color="primary" @click="viewEmployee(item)" outlined>
          View
        </v-btn>
      </template>
    </v-data-table>
    <v-dialog v-model="dialogEmployee" persistent max-width="490">
      <v-card>
        <v-card-title class="text-h5">
          {{ dialogEmployeeAction }} Employee
        </v-card-title>
        <v-card-text>
          <v-text-field
            label="Barcode ID"
            v-model="dialogEmployeeRecord.barcodeId"
            color="primary"
            outlined
            class="tool-run-time"
            :rules="[(v) => checkBarcodeId(v)]"
            prepend-icon="badge"
          >
          </v-text-field>
          <v-text-field
            label="Payroll ID"
            v-model="dialogEmployeeRecord.payroll.payrollId"
            color="primary"
            outlined
            class="tool-run-time"
            prepend-icon="tag"
          >
          </v-text-field>
          <v-text-field
            label="First Name"
            v-model="dialogEmployeeRecord.firstName"
            type="text"
            color="primary"
            outlined
            class="tool-run-time"
            prepend-icon="person"
          >
          </v-text-field>
          <v-text-field
            label="Last Name"
            v-model="dialogEmployeeRecord.lastName"
            type="text"
            color="primary"
            outlined
            class="tool-run-time"
            prepend-icon="person"
          >
          </v-text-field>
          <v-select
            label="Department Code"
            v-model="dialogEmployeeRecord.department"
            :items="[
              'demolition',
              'core & shell',
              'concrete & steel',
              'foreman',
              'office',
              'mechanic',
              'asbestos',
              'supervisor',
              'burner',
              'driver',
              'operator',
              'electric',
              'fireproofing',
              'structuretech',
            ]"
            type="text"
            color="primary"
            outlined
            class="tool-run-time"
            prepend-icon="handyman"
          >
          </v-select>
          <v-text-field
            label="Start Date"
            v-model="dialogEmployeeRecord.startDate"
            color="primary"
            outlined
            class="tool-run-time"
            prepend-icon="date_range"
            type="date"
          >
          </v-text-field>
          <hr />
          <br />
          <div v-if="dialogEmployeeAction === 'Edit'">
            <strong>Terminate Employee</strong>
            <p class="red--text caption">
              This will remove an employee from all site-wide activities.
            </p>
            <v-text-field
              label="Terminate Date"
              v-model="dialogEmployeeRecord.terminateDate"
              color="primary"
              outlined
              class="tool-run-time"
              prepend-icon="person_off"
              type="date"
              clearable
            >
            </v-text-field>
            <hr />
            <br />
          </div>
          <div v-if="dialogEmployeeRecord.payroll.rateHistory.length">
            <strong>Rate History</strong>
            <br />
            <br />
            <v-row wrap class="justify-left">
              <v-col cols="4" xs="4" class="pb-0">
                <strong>Rate</strong>
              </v-col>
              <v-col cols="4" xs="4" class="pb-0">
                <strong>Effective Date</strong>
              </v-col>
              <v-col cols="4" xs="4" class="pb-0">
                <strong>Delete</strong>
              </v-col>
            </v-row>
            <v-row
              wrap
              class="justify-left"
              v-for="r in dialogEmployeeRecord.payroll.rateHistory"
              :key="r.effectiveDate.seconds"
            >
              <v-col cols="4" xs="4" class="pb-0">
                <div>{{ r.rate }}</div>
              </v-col>
              <v-col cols="4" xs="4" class="pb-0">
                <div>{{ r.effectiveDate | moment("MMM Do YYYY") }}</div>
              </v-col>
              <v-col cols="4" xs="4" class="pb-0">
                <v-checkbox
                  style="margin-top: -5px;"
                  color="primary"
                  v-model="r.delete"
                  :value="false"
                ></v-checkbox>
              </v-col>
            </v-row>
          </div>
          <strong>New Rate</strong>
          <br />
          <br />
          <v-text-field
            label="Effective Date"
            v-model="dialogEmployeeNewRate.effectiveDate"
            type="date"
            color="primary"
            outlined
            class="tool-run-time"
            prepend-icon="mdi-calendar"
          ></v-text-field>
          <v-text-field
            label="Rate"
            v-model="dialogEmployeeNewRate.rate"
            type="number"
            color="primary"
            outlined
            class="tool-run-time"
            prepend-icon="attach_money"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialogEmployee = false">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="saveEmployeeDialog"
            :loading="dialogEmployeeLoading"
            :disabled="dialogEmployeeLoading"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import fb from "@/main";
import { mapState } from "vuex";
import moment from "moment";

export default {
  data: () => {
    return {
      loading: false,
      employees: [],
      activeEmployees: [],
      terminatedEmployees: [],
      employeeTab: "active",
      dialogEmployee: false,
      dialogEmployeeRecord: { payroll: { rateHistory: [] } },
      dialogEmployeeAction: "",
      dialogEmployeeNewRate: { effectiveDate: undefined, rate: null },
      dialogEmployeeLoading: false,
      snack: false,
      snackText: "",
      snackColor: undefined,
      search: "",
      filters: {
        rateMin: null,
        rateMax: null,
      },
      headers: [
        {
          text: "Badge #",
          align: "start",
          value: "barcodeId",
        },
        {
          text: "Name",
          align: "start",
          value: "firstName",
        },
        {
          text: "Payroll ID",
          align: "start",
          value: "payrollId",
        },
        {
          text: "Department Code",
          align: "start",
          value: "department",
        },
        {
          text: "Rate",
          align: "start",
          sortable: false,
          value: "rate",
        },
        {
          text: "Rate Effective Date",
          align: "start",
          sortable: false,
          value: "effectiveDate",
        },
        {
          text: "Start Date",
          align: "start",
          value: "startDate",
        },
        {
          text: "Edit",
          align: "start",
          sortable: false,
          value: "edit",
        },
        {
          text: "",
          align: "start",
          sortable: false,
          value: "view",
        },
      ],
    };
  },
  computed: {
    ...mapState([
      "user",
      "allEmployees",
      "allPayroll",
      "employeesLoaded",
      "payrollLoaded",
    ]),
    filteredEmployees: function() {
      let result = this.employees;
      if (this.filters.rateMin !== null && this.filters.rateMin !== "") {
        result = result.filter(
          (e) => e.rate !== null && e.rate >= parseFloat(this.filters.rateMin),
        );
      }
      if (this.filters.rateMax !== null && this.filters.rateMax !== "") {
        result = result.filter(
          (e) => e.rate !== null && e.rate <= parseFloat(this.filters.rateMax),
        );
      }
      return result;
    },
  },
  watch: {
    // Watch for changes in store data and reprocess when both are loaded
    employeesLoaded(newVal) {
      if (newVal && this.payrollLoaded) {
        this.processEmployeeData();
      }
    },
    payrollLoaded(newVal) {
      if (newVal && this.employeesLoaded) {
        this.processEmployeeData();
      }
    },
  },
  methods: {
    filterByName(value, search, item) {
      const searchTerm = search.toLowerCase();
      const barcodeId = (item.barcodeId || "").toString();
      return (
        item.firstName.toLowerCase().indexOf(searchTerm) > -1 ||
        item.lastName.toLowerCase().indexOf(searchTerm) > -1 ||
        item.department.toLowerCase().indexOf(searchTerm) > -1 ||
        barcodeId.indexOf(searchTerm) > -1
      );
    },
    showEmployeeDialog: function(action, record) {
      this.dialogEmployeeAction = action;
      this.dialogEmployeeRecord = { payroll: { rateHistory: [] } };
      this.dialogEmployeeNewRate = { effectiveDate: undefined, rate: null };
      if (record) {
        this.dialogEmployeeRecord = { ...record };
      }
      this.dialogEmployee = true;
    },
    checkBarcodeId: function(barcodeId) {
      const action = this.dialogEmployeeAction;
      const foundEmployee = this.employees.find(
        (e) => barcodeId && barcodeId == e.barcodeId && action === "Add",
      );
      if (foundEmployee) {
        return `Warning: ${barcodeId} already belongs to ${foundEmployee.firstName} ${foundEmployee.lastName}.`;
      } else {
        return true;
      }
    },
    saveEmployeeDialog: async function() {
      const record = this.dialogEmployeeRecord;
      const action = this.dialogEmployeeAction;
      const payroll = record.payroll;

      try {
        const preventSaveAdd = !!this.employees.find(
          (e) =>
            record.barcodeId &&
            record.barcodeId == e.barcodeId &&
            action === "Add",
        );
        const preventSaveEdit = !!this.employees.find(
          (e) =>
            record.barcodeId &&
            record.barcodeId == e.barcodeId &&
            action === "Edit" &&
            e.docId !== record.docId,
        );
        if (preventSaveAdd || preventSaveEdit) {
          alert(`This barcode ID is already assigned to someone else.`);
          return;
        }

        const missingValues = [];
        if (!record.firstName) {
          missingValues.push("First Name");
        }
        if (!record.lastName) {
          missingValues.push("Last Name");
        }
        if (!payroll.payrollId) {
          missingValues.push("Payroll ID");
        }
        if (!record.department) {
          missingValues.push("Department Code");
        }
        if (!record.startDate) {
          missingValues.push("Start Date");
        }
        if (missingValues.length) {
          alert(
            `Please provide the missing values: ${missingValues.join(", ")}`,
          );
          return;
        }

        const newRate = this.dialogEmployeeNewRate;
        const rateHistory = [...payroll.rateHistory].filter(
          (r) => r && !r.delete,
        );

        if (newRate.rate && newRate.effectiveDate) {
          rateHistory.push({
            effectiveDate: moment(newRate.effectiveDate).format("YYYY-MM-DD"),
            rate: parseFloat(newRate.rate),
          });
        }

        if (action === "Add") {
          this.dialogEmployeeLoading = true;
          const addRes = await fb.db.collection("payroll").add({
            rateHistory,
            payrollId: payroll.payrollId,
          });
          const autoDocId = addRes.id;
          const obj = {
            firstName: record.firstName,
            lastName: record.lastName,
            department: record.department,
            startDate: record.startDate,
            terminateDate: record.terminateDate || null,
          };
          if (record.barcodeId > 0) {
            obj.barcodeId = record.barcodeId;
          }
          obj.lastModifiedBy = this.user.email || null;
          const empRes = await fb.db
            .collection("employees")
            .doc(autoDocId)
            .set(obj);
        } else if (action === "Edit") {
          this.dialogEmployeeLoading = true;
          const payRes = await fb.db
            .collection("payroll")
            .doc(record.docId)
            .update({
              rateHistory,
              payrollId: payroll.payrollId,
            });
          const obj = {
            firstName: record.firstName,
            lastName: record.lastName,
            department: record.department,
            startDate: record.startDate,
            terminateDate: record.terminateDate || null,
          };
          if (record.barcodeId > 0) {
            obj.barcodeId = record.barcodeId;
          }
          obj.lastModifiedBy = this.user.email;
          const empRes = await fb.db
            .collection("employees")
            .doc(record.docId)
            .update(obj);
        }
        this.snack = true;
        this.snackText = `${action} was successful for ${record.firstName} ${record.lastName}.`;
        this.snackColor = "success";
        // No need to call getEmployees() since store will auto-update via real-time listeners
        this.dialogEmployee = false;
      } catch (e) {
        this.snack = true;
        this.snackText = `${action} had an error for ${record.firstName} ${record.lastName}.`;
        this.snackColor = "error";
        console.log("saveEmployeeDialog error", e);
      }
      this.dialogEmployeeLoading = false;
    },
    getEmployees: async function() {
      this.loading = true;

      // If both datasets are already loaded, process immediately
      if (this.employeesLoaded && this.payrollLoaded) {
        this.processEmployeeData();
        return;
      }

      // Load employee and payroll data from store if not already loaded
      if (!this.employeesLoaded) {
        await this.$store.dispatch("getAllEmployees", fb);
      }
      if (!this.payrollLoaded) {
        await this.$store.dispatch("getAllPayroll", fb);
      }

      // Process will be triggered by watchers when both are loaded
    },
    processEmployeeData: async function() {
      // Combine employee and payroll data (using cached store data)
      const employees = this.allEmployees.map((emp) => {
        const employee = { ...emp };

        // Add payroll information
        const pay =
          this.allPayroll.find((p) => p.docId === employee.docId) || {};
        employee.payroll = pay;

        // Add rate information
        pay.rateHistory = pay.rateHistory || [];
        const rateHistory = pay.rateHistory.sort(function(a, b) {
          return new Date(b.effectiveDate) - new Date(a.effectiveDate);
        });
        employee.rate = null;
        employee.effectiveDate = undefined;
        if (rateHistory.length) {
          employee.rate = rateHistory[0].rate;
          employee.effectiveDate = rateHistory[0].effectiveDate;
        }

        return employee;
      });

      // Filter employees into categories
      this.activeEmployees = employees.filter((emp) => {
        return !emp.terminateDate;
      });

      this.terminatedEmployees = employees.filter((emp) => {
        return emp.terminateDate;
      });

      this.switchEmployeeTab(this.employeeTab);
      this.loading = false;
    },
    viewEmployee: function(employee) {
      this.$router.push(`/employees/${employee.docId}`);
    },
    switchEmployeeTab: function(tab) {
      this.employeeTab = tab;
      if (tab === "active") {
        this.employees = this.activeEmployees;
      } else if (tab === "terminated") {
        this.employees = this.terminatedEmployees;
      }
    },
    downloadEmployeesCSV: function() {
      // Get the current employees based on the active tab
      const employees = this.employees;
      const tabName = this.employeeTab;

      if (!employees || employees.length === 0) {
        this.snack = true;
        this.snackText = "No employees to download";
        this.snackColor = "warning";
        return;
      }

      // Define CSV headers
      const headers = [
        "Badge #",
        "First Name",
        "Last Name",
        "Payroll ID",
        "Department Code",
        "Rate",
        "Rate Effective Date",
        "Start Date",
        "Terminate Date",
      ];

      // Create CSV rows
      const rows = employees.map((emp) => {
        return [
          emp.barcodeId || "",
          emp.firstName || "",
          emp.lastName || "",
          emp.payroll.payrollId || "",
          emp.department || "",
          emp.rate || "",
          emp.effectiveDate
            ? moment(emp.effectiveDate).format("MMM Do YYYY")
            : "",
          emp.startDate || "",
          emp.terminateDate || "",
        ];
      });

      // Combine headers and rows
      const csvContent = [
        headers.join(","),
        ...rows.map((row) =>
          row
            .map((field) => {
              // Escape fields containing commas or quotes
              const fieldStr = String(field);
              if (
                fieldStr.includes(",") ||
                fieldStr.includes('"') ||
                fieldStr.includes("\n")
              ) {
                return `"${fieldStr.replace(/"/g, '""')}"`;
              }
              return fieldStr;
            })
            .join(","),
        ),
      ].join("\n");

      // Create blob and download
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `employees_${tabName}_${moment().format("YYYY-MM-DD")}.csv`,
      );
      link.style.visibility = "hidden";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      this.snack = true;
      this.snackText = `Downloaded ${employees.length} ${tabName} employee(s)`;
      this.snackColor = "success";
    },
  },
  mounted: async function() {
    this.getEmployees();
  },
};
</script>
